import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface GSProduct {
  id: string;
  name: string;
  description: string | null;
  product_type: string;
  certification_status: string;
  manufacturer: string | null;
  created_at: string;
  portfolio: {
    id: string;
    title: string;
  };
}

export const useGSProducts = () => {
  const [products, setProducts] = useState<GSProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error: fetchError } = await supabase
        .from('products')
        .select(`
          id,
          name,
          description,
          product_type,
          certification_status,
          manufacturer,
          created_at,
          portfolio:portfolios!inner(
            id,
            title
          )
        `)
        .eq('portfolio.user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const addProduct = async (productData: {
    name: string;
    description?: string;
    product_type: string;
    manufacturer?: string;
    portfolio_id: string;
  }) => {
    try {
      const { data, error: insertError } = await supabase
        .from('products')
        .insert([productData])
        .select(`
          id,
          name,
          description,
          product_type,
          certification_status,
          manufacturer,
          created_at,
          portfolio:portfolios(
            id,
            title
          )
        `)
        .single();

      if (insertError) throw insertError;

      // Refresh the products list
      await fetchProducts();
      
      return data;
    } catch (err) {
      console.error('Error adding product:', err);
      throw err;
    }
  };

  return {
    products,
    loading,
    error,
    addProduct,
    refetch: fetchProducts,
  };
};